import React, { useEffect, useRef, useState } from "react";

const IOSInstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const installPromptRef = useRef(null);

  useEffect(() => {
    const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isAndroid = () => /android/i.test(navigator.userAgent);
    const isInStandaloneMode = () =>
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    const detectedPlatform = isIos() ? "ios" : isAndroid() ? "android" : null;

    if (!detectedPlatform || isInStandaloneMode()) {
      return undefined;
    }

    const dismissedKey = `${detectedPlatform}PromptDismissed`;
    if (sessionStorage.getItem(dismissedKey)) {
      return undefined;
    }

    setPlatform(detectedPlatform);

    const handleBeforeInstallPrompt = event => {
      if (detectedPlatform !== "android") return;
      event.preventDefault();
      installPromptRef.current = event;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      sessionStorage.setItem(dismissedKey, "1");
      installPromptRef.current = null;
      setCanInstall(false);
      setShow(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    const timer = setTimeout(() => setShow(true), 3000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    if (platform) {
      sessionStorage.setItem(`${platform}PromptDismissed`, "1");
    }
    setShow(false);
  };

  const installOnAndroid = async () => {
    const installPrompt = installPromptRef.current;
    if (!installPrompt) return;

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    installPromptRef.current = null;
    setCanInstall(false);

    if (choice?.outcome === "accepted") {
      sessionStorage.setItem("androidPromptDismissed", "1");
    }
    setShow(false);
  };

  const isAndroid = platform === "android";

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99999,
      background: "#1a1a2e", borderTop: "1px solid rgba(255,255,255,0.12)",
      borderRadius: "16px 16px 0 0", padding: "16px 20px 28px",
      boxShadow: "0 -8px 32px rgba(0,0,0,0.5)",
      animation: "iosSlideUp 0.35s ease-out",
      fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{"@keyframes iosSlideUp { from { transform:translateY(100%); opacity:0 } to { transform:translateY(0); opacity:1 } }"}</style>
      <div style={{ width:36, height:4, borderRadius:2, background:"rgba(255,255,255,0.2)", margin:"0 auto 16px" }} />
      <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
        <img src="/android-chrome-192x192.png" alt="Estratiza Hub"
          style={{ width:52, height:52, borderRadius:12, flexShrink:0 }} />
        <div style={{ flex:1 }}>
          <p style={{ margin:"0 0 4px", fontWeight:700, fontSize:15, color:"#fff" }}>
            Instalar Estratiza Hub
          </p>
          <p style={{ margin:"0 0 12px", fontSize:13, color:"rgba(255,255,255,0.6)", lineHeight:1.4 }}>
            {isAndroid
              ? "Instale na tela inicial para usar como app — acesso mais rápido e em tela cheia."
              : "Adicione à tela inicial para usar como app — abre em tela cheia sem a barra do Safari."}
          </p>
          {isAndroid ? (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {canInstall ? (
                <button
                  type="button"
                  onClick={installOnAndroid}
                  style={{
                    alignSelf:"flex-start", border:0, borderRadius:10,
                    background:"#3b82f6", color:"#fff", cursor:"pointer",
                    fontSize:13, fontWeight:700, padding:"10px 16px",
                  }}
                >
                  Instalar agora
                </button>
              ) : (
                <>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"rgba(59,130,246,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                      color:"#3b82f6", fontSize:20, fontWeight:700, lineHeight:1 }}>
                      ⋮
                    </div>
                    <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>
                      Toque no menu <strong style={{ color:"#3b82f6" }}>⋮</strong> do Chrome
                    </span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"rgba(59,130,246,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <line x1="12" y1="8" x2="12" y2="16"/>
                        <line x1="8" y1="12" x2="16" y2="12"/>
                      </svg>
                    </div>
                    <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>
                      Selecione <strong style={{ color:"#3b82f6" }}>Instalar app</strong> ou <strong style={{ color:"#3b82f6" }}>Adicionar à tela inicial</strong>
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:28, height:28, borderRadius:8, background:"rgba(59,130,246,0.15)",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                    <polyline points="16 6 12 2 8 6"/>
                    <line x1="12" y1="2" x2="12" y2="15"/>
                  </svg>
                </div>
                <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>
                  Toque em <strong style={{ color:"#3b82f6" }}>Compartilhar</strong> na barra do Safari
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:28, height:28, borderRadius:8, background:"rgba(59,130,246,0.15)",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="12" y1="8" x2="12" y2="16"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                  </svg>
                </div>
                <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>
                  Selecione <strong style={{ color:"#3b82f6" }}>Adicionar à Tela de Início</strong>
                </span>
              </div>
            </div>
          )}
        </div>
        <button onClick={dismiss} style={{
          background:"none", border:"none", cursor:"pointer",
          color:"rgba(255,255,255,0.4)", fontSize:22, lineHeight:1,
          padding:0, flexShrink:0, marginTop:-2,
        }} aria-label="Fechar">×</button>
      </div>
    </div>
  );
};

export default IOSInstallPrompt;
